// Vietnam Provinces API Service using OAPI
const OAPI_BASE_URL = 'https://open.oapi.vn/location';

// Cache for API responses to avoid redundant requests
const cache = {
  provinces: null,
  districts: {},
  wards: {}
};

// Timeout utility function
const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Kết nối quá chậm. Vui lòng thử lại sau.');
    }
    throw error;
  }
};

const ProvincesService = {
  // Get all provinces
  getProvinces: async () => {
    try {
      // Return cached data if available
      if (cache.provinces) {
        return cache.provinces;
      }

      console.log('Fetching provinces from OAPI...');
      const response = await fetchWithTimeout(`${OAPI_BASE_URL}/provinces?page=0&size=120`);
      
      if (!response.ok) {
        throw new Error(`API Error (${response.status}): Không thể tải danh sách tỉnh thành`);
      }

      const result = await response.json();
      
      // Check if API response is successful
      if (result.code !== 'success' || !result.data) {
        throw new Error('Invalid API response format');
      }
      
      // Transform data to match existing format (using id as code)
      const provinces = result.data.map(province => ({
        code: province.id,
        name: province.name,
        slug: province.slug,
        type: province.type,
        typeText: province.typeText
      }));

      // Cache the result
      cache.provinces = provinces;
      
      console.log('Provinces loaded successfully:', provinces.length);
      return provinces;
    } catch (error) {
      console.error('Error fetching provinces:', error);
      throw new Error('Không thể tải danh sách tỉnh thành. Vui lòng thử lại.');
    }
  },

  // Get districts by province ID
  getDistrictsByProvince: async (provinceId) => {
    try {
      // Return cached data if available
      if (cache.districts[provinceId]) {
        return cache.districts[provinceId];
      }

      console.log('Fetching districts for province ID:', provinceId);
      const response = await fetchWithTimeout(`${OAPI_BASE_URL}/districts/${provinceId}?page=0&size=120`);
      
      if (!response.ok) {
        throw new Error(`API Error (${response.status}): Không thể tải danh sách quận huyện`);
      }

      const result = await response.json();
      
      // Check if API response is successful
      if (result.code !== 'success' || !result.data) {
        throw new Error('Invalid API response format');
      }
      
      // Transform data to match existing format
      const districts = result.data.map(district => ({
        code: district.id,
        name: district.name,
        provinceId: district.provinceId,
        type: district.type,
        typeText: district.typeText
      }));

      // Cache the result
      cache.districts[provinceId] = districts;
      
      console.log('Districts loaded successfully:', districts.length);
      return districts;
    } catch (error) {
      console.error('Error fetching districts:', error);
      throw new Error('Không thể tải danh sách quận huyện. Vui lòng thử lại.');
    }
  },

  // Get wards by district ID
  getWardsByDistrict: async (districtId) => {
    try {
      // Return cached data if available
      if (cache.wards[districtId]) {
        return cache.wards[districtId];
      }

      console.log('Fetching wards for district ID:', districtId);
      const response = await fetchWithTimeout(`${OAPI_BASE_URL}/wards/${districtId}?page=0&size=200`);
      
      if (!response.ok) {
        throw new Error(`API Error (${response.status}): Không thể tải danh sách phường xã`);
      }

      const result = await response.json();
      
      // Check if API response is successful
      if (result.code !== 'success' || !result.data) {
        throw new Error('Invalid API response format');
      }
      
      // Transform data to match existing format
      const wards = result.data.map(ward => ({
        code: ward.id,
        name: ward.name,
        districtId: ward.districtId,
        type: ward.type,
        typeText: ward.typeText
      }));

      // Cache the result
      cache.wards[districtId] = wards;
      
      console.log('Wards loaded successfully:', wards.length);
      return wards;
    } catch (error) {
      console.error('Error fetching wards:', error);
      throw new Error('Không thể tải danh sách phường xã. Vui lòng thử lại.');
    }
  },

  // Clear cache (useful for testing or if data needs to be refreshed)
  clearCache: () => {
    cache.provinces = null;
    cache.districts = {};
    cache.wards = {};
    console.log('Provinces cache cleared');
  }
};

export default ProvincesService;

